import React, { useState, useRef, useEffect } from "react";
import cn from "classnames";
import styles from "./ConversationList.module.sass";
import ConversationItem from "./ConversationItem";
import Icon from "../../Icon";
import { useChatContext } from "../../../contexts/ChatContext";

// Updated: User selection UX - handle as primary, first name as secondary
const ConversationList = ({ 
    conversations, 
    activeConversationId,
    onConversationSelect,
    onNewMessage,
    // Group button removed per new spec
    isSearchingUsers,
    userSearchResults = [],
    onUserSearch,
    onSelectUser,
    onCancelSearch,
    className 
}) => {
    const { deleteChannel } = useChatContext();
    const [searchQuery, setSearchQuery] = useState("");
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [selectedConversation, setSelectedConversation] = useState(null);
    const contextMenuRef = useRef(null);
    
    // Sort conversations: unread first, then by most recent
    const sortedConversations = [...conversations].sort((a, b) => {
        if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
        if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
        return new Date(b.timestamp) - new Date(a.timestamp);
    });
    
    // Filter conversations based on search
    const filteredConversations = sortedConversations.filter(conv =>
        conv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (conv.lastMessage && conv.lastMessage.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    
    const handleContextMenu = (e, conversation) => {
        e.preventDefault();
        setSelectedConversation(conversation);
        setContextMenuPosition({ x: e.clientX, y: e.clientY });
        setShowContextMenu(true);
    };
    
    const handleContextMenuAction = async (action) => {
        console.log(`${action} conversation:`, selectedConversation);
        setShowContextMenu(false);
        
        switch(action) {
            case 'archive':
                // Handle archive - to be implemented
                console.log('Archive not yet implemented');
                break;
            case 'mute':
                // Handle mute - to be implemented
                console.log('Mute not yet implemented');
                break;
            case 'delete':
                // Handle delete
                if (selectedConversation && window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
                    try {
                        await deleteChannel(selectedConversation.id);
                        // Conversation will be removed from UI by the context
                    } catch (error) {
                        console.error('Failed to delete conversation:', error);
                        alert('Failed to delete conversation. Please try again.');
                    }
                }
                break;
            default:
                break;
        }
    };
    
    // Close context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
                setShowContextMenu(false);
            }
        };
        
        if (showContextMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showContextMenu]);
    
    return (
        <div className={cn(styles.conversationList, className)}>
            <div className={styles.header}>
                <div className={styles.searchContainer}>
                    <Icon name="search" size="20" />
                    <input
                        type="text"
                        placeholder="Search conversations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>
                <div className={styles.actions}>
                    <button 
                        className={cn(styles.fabNew)}
                        onClick={onNewMessage}
                        title="New Message"
                    >
                        <Icon name="plus" size="20" />
                    </button>
                </div>
            </div>

            {/* New Message Search Overlay */}
            {isSearchingUsers && (
                <div className={styles.newMessageOverlay}>
                    <div className={styles.overlayHeader}>
                        <div className={styles.overlaySearch}>
                            <Icon name="search" size="18" />
                            <input
                                type="text"
                                autoFocus
                                placeholder="Search creators and hosts..."
                                onChange={(e) => onUserSearch && onUserSearch(e.target.value)}
                            />
                        </div>
                        <button
                            className={styles.overlayClose}
                            onClick={onCancelSearch}
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>
                    <div className={styles.overlayList}>
                        {userSearchResults.length === 0 ? (
                            <div className={styles.overlayEmpty}>Start typing to search users…</div>
                        ) : (
                            userSearchResults.map((u) => {
                                const handle = u.handle || u.username ? `@${(u.handle || u.username).replace(/^@/, '')}` : null;
                                const firstName = u.first_name || (u.name ? u.name.split(' ')[0] : '');

                                return (
                                    <button
                                        key={u.id}
                                        className={styles.overlayItem}
                                        onClick={() => onSelectUser && onSelectUser(u)}
                                    >
                                        <img src={u.image || "/images/content/avatar-1.jpg"} alt={u.name} />
                                        <div className={styles.overlayItemInfo}>
                                            <div className={styles.overlayName}>
                                                {handle ? (
                                                    <>
                                                        <span style={{ color: '#0f084b' }}>{handle}</span>
                                                        <span style={{ marginLeft: '8px', opacity: 0.7, fontWeight: '400', fontSize: '14px', color: '#666' }}>{firstName}</span>
                                                    </>
                                                ) : (
                                                    u.name
                                                )}
                                            </div>
                                            {u.role && <div className={styles.overlayRole}>{u.role}</div>}
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
            
            <div className={styles.conversations}>
                {filteredConversations.length > 0 ? (
                    filteredConversations.map(conversation => (
                        <ConversationItem
                            key={conversation.id}
                            conversation={conversation}
                            isActive={conversation.id === activeConversationId}
                            onClick={onConversationSelect}
                            onContextMenu={handleContextMenu}
                        />
                    ))
                ) : (
                    <div className={styles.emptyState}>
                        {searchQuery ? (
                            <>
                                <Icon name="search" size="48" />
                                <p>No conversations found</p>
                                <span>Try adjusting your search</span>
                            </>
                        ) : (
                            <>
                                <Icon name="comment" size="48" />
                                <p>No conversations yet</p>
                                <span>Start a new message to begin chatting</span>
                            </>
                        )}
                    </div>
                )}
            </div>
            
            {/* Context Menu */}
            {showContextMenu && (
                <div 
                    ref={contextMenuRef}
                    className={styles.contextMenu}
                    style={{ 
                        top: contextMenuPosition.y,
                        left: contextMenuPosition.x 
                    }}
                >
                    <button 
                        className={styles.contextMenuItem}
                        onClick={() => handleContextMenuAction('archive')}
                    >
                        <Icon name="folder" size="16" />
                        Archive
                    </button>
                    <button 
                        className={styles.contextMenuItem}
                        onClick={() => handleContextMenuAction('mute')}
                    >
                        <Icon name="bell" size="16" />
                        Mute
                    </button>
                    <div className={styles.contextMenuDivider} />
                    <button 
                        className={cn(styles.contextMenuItem, styles.danger)}
                        onClick={() => handleContextMenuAction('delete')}
                    >
                        <Icon name="trash" size="16" />
                        Delete
                    </button>
                </div>
            )}
        </div>
    );
};

export default ConversationList;
